/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  BASE_PRICE_COEFFICIENT,
  BUSINESS_TYPE,
  CURRENCY,
  DEFAULT_CROP_LIMIT,
  DISCOUNT_ENUM,
  LBS_KG_FACTOR,
  MT_KG_FACTOR,
  OZ_KG_FACTOR,
  SCREEN_TYPE,
  SUPPLIER_TYPE,
  SUPPLY_TYPE_ENUM,
  TRANSPORTATION_MODE_ENUM,
  WEIGHT_SYSTEM
} from './constants';
import { chipVariantType } from './types';
import { IAddressForm } from './types/address';
import { ICustomerDetailForm, ICustomerInfo } from './types/customer';
import {
  IBankDetailForm,
  IBankdetail,
  ISupplier,
  ISupplierDetailForm,
  ISupplierFilterOptions
} from './types/supplier';
import moment, { Moment } from 'moment';
import { endOfDay, format } from 'date-fns';
import { DATE_FORMAT, ADDRESS_TYPE_CONSTANTS } from '@helpers/constants';
import { getToken, parseJWT } from './auth-helper';
import SessionService from './session-helper';
import { getDocumentLink } from '@services/util.service';
import notify from './toastify-helper';
import _ from 'lodash';
import { ICurrency, IWeightChildOption } from './types/quotes';

export function getAddressesValue(param: IAddressForm[], same_as_office: boolean) {
  const addresses = param.map((address, index) => ({
    ...(address.address_id && { address_id: address.address_id }),
    address_type: address.address_type,
    address_line1: address.address_line1,
    address_line2: address.address_line2,
    zip_code: address.zip_code,
    city: `${address.city?.value}`,
    state: `${address.state ? address.state?.value : ''}`,
    country: `${address.country?.value}`,
    same_as_office: index === 0 ? same_as_office : false,
    address_suffix: address.address_suffix ? address.address_suffix : null
  }));
  return addresses;
}

export function getSupplierDetailFormValuesFromResponse(
  param: ISupplier,
  filterOptions?: ISupplierFilterOptions
): ISupplierDetailForm {
  let same_as_office = false;
  let officeAddress: IAddressForm = {
    address_id: '',
    address_line1: '',
    address_line2: '',
    address_type: '',
    zip_code: '',
    city: null,
    state: null,
    country: null,
    address_suffix: null
  };
  const factoryAddress: IAddressForm[] = [];
  param.addresses.forEach((address) => {
    const adr = {
      address_id: address.address_id,
      address_line1: address.address_line1,
      address_line2: address.address_line2,
      address_type: address.address_type,
      zip_code: address.zip_code,
      city: address.city_obj,
      state: address.state_obj,
      country: address.country_obj,
      address_suffix: address.address_suffix
    };
    if (address.same_as_office) same_as_office = true;
    if (address.address_type === 'OFC') {
      officeAddress = adr;
      return;
    }
    factoryAddress.push(adr);
  });
  return {
    supplier_id: param.supplier_id,
    supplier_name: param.supplier_name,
    gst_number: param.gst_number,
    supplier_email: param.supplier_email,
    supplier_finance_email_id: param.finance_department_email ?? '',
    supplier_website: param.supplier_website,
    supplier_phone_number: param.supplier_phone_number,
    supplier_landline: param.supplier_landline,
    supplier_type:
      filterOptions?.supplier_origin_filter_options?.find(
        (item: any) => item.value == param.supplier_type
      ) ?? null,
    business_type:
      filterOptions?.business_type_filter_options?.find(
        (item: any) => item.value == param.business_type
      ) ?? null,
    vendor_id: param.vendor_id,
    addresses: factoryAddress,
    same_as_office: same_as_office,
    pocs: param.supplier_pocs,
    type:
      filterOptions?.supplier_type_filter_options?.find((item: any) => item.value == param.type) ??
      null,
    msme_no: param.msme_no,
    isSezorEou: param.is_sez_or_eou ?? false,
    is_tnc_not_needed: param.is_tnc_not_needed ?? false,
    ...officeAddress
  };
}

export function getRequestBodyForSupplierDetail(param: ISupplierDetailForm) {
  const addresses = getAddressesValue(param.addresses, param.same_as_office);
  addresses.push({
    ...(param.address_id && { address_id: param.address_id }),
    address_type: 'OFC',
    address_line1: param.address_line1,
    address_line2: param.address_line2,
    zip_code: param.zip_code,
    city: `${param.city?.value}`,
    state: `${param.state ? param.state?.value : ''}`,
    country: `${param.country?.value}`,
    same_as_office: false,
    address_suffix: param.address_suffix ? param.address_suffix : null
  });
  return {
    supplier_name: param.supplier_name,
    gst_number: param.gst_number,
    supplier_email: param.supplier_email,
    supplier_website: param.supplier_website,
    supplier_phone_number: param.supplier_phone_number,
    supplier_landline: param.supplier_landline,
    supplier_type: `${param.supplier_type?.value}`,
    business_type: `${param.business_type?.value}`,
    addresses: addresses,
    bankdetails: [],
    documents: [],
    supplier_pocs: param.pocs,
    type: param.type?.value,
    msme_no: param?.msme_no,
    finance_department_email: param.supplier_finance_email_id,
    is_sez_or_eou: param.isSezorEou,
    is_tnc_not_needed: param.is_tnc_not_needed
  };
}

export function getRequestBodyForSupplierBankDetail(param: IBankDetailForm) {
  const data = param.bankdetails.map((item) => {
    const { bank_id, ...rest } = item;
    return {
      ...rest,
      ...(bank_id && { bank_id: bank_id }),
      city: `${item.city?.value}`,
      state: `${item.state ? item.state?.value : ''}`,
      country: `${item.country?.value}`,
      address_suffix: item.address_suffix ? item.address_suffix : null
    };
  });
  return {
    bankdetails: data
  };
}

export function getBankFormValuesFromResponse(bankdetails: IBankdetail[]) {
  const bankInfo = bankdetails.map((info) => {
    const { updated_at, created_at, city, state, country, ...rest } = info;
    return {
      ...rest,
      branch_name: rest.branch_name ?? '',
      city: rest.city_obj,
      state: rest.state_obj,
      country: rest.country_obj,
      address_suffix: rest.address_suffix
    };
  });
  return bankInfo;
}

export function getCustomerFormValuesFromResponse(param: ICustomerInfo): ICustomerDetailForm {
  const business_type = BUSINESS_TYPE.find(
    (item) => item.value.toUpperCase() === param.business_type.toUpperCase()
  );
  const pocs = param.pocs;

  let same_as_office = false;
  let officeAddress: IAddressForm = {
    address_id: '',
    address_line1: '',
    address_line2: '',
    address_type: '',
    zip_code: '',
    city: null,
    state: null,
    country: null,
    address_suffix: null
  };
  const factoryAddress: IAddressForm[] = [];
  param.addresses.forEach((address) => {
    const adr = {
      address_id: address.address_id,
      address_line1: address.address_line1,
      address_line2: address.address_line2,
      address_type: address.address_type,
      zip_code: address.zip_code,
      city: address.city_obj,
      state: address.state_obj,
      country: address.country_obj,
      address_suffix: address.address_suffix
    };
    if (address.same_as_office) same_as_office = true;
    if (address.address_type === 'OFC') {
      officeAddress = adr;
      return;
    }
    factoryAddress.push(adr);
  });
  const data = {
    customer_id: param.customer_id,
    name: param.name,
    number: param.number,
    email: param.email,
    website: param.website,
    business_type: business_type!,
    customer_country: { label: param.country, value: param.country },
    remarks: param.remarks,
    same_as_office: same_as_office,
    pocs: pocs,
    addresses: factoryAddress,
    is_tnc_not_needed: param.is_tnc_not_needed,
    ...officeAddress
  };
  return data;
}

export function getRequestBodyForCustomerDetail(data: ICustomerDetailForm) {
  const addresses = getAddressesValue(data.addresses, data.same_as_office);
  addresses.push({
    ...(data.address_id && { address_id: data.address_id }),
    address_type: 'OFC',
    address_line1: data.address_line1,
    address_line2: data.address_line2,
    zip_code: data.zip_code,
    city: `${data.city?.value}`,
    state: `${data.state ? data.state?.value : ''}`,
    country: `${data.country?.value}`,
    same_as_office: false,
    address_suffix: data.address_suffix ? data.address_suffix : null
  });
  const pocs = data.pocs.map((item) => {
    const { uid, email, ...rest } = item;
    return {
      ...(uid && { uid: uid }),
      ...rest,
      ...(!uid && { email: email })
    };
  });
  const formData: any = {
    name: data.name,
    description: data.remarks ?? '',
    remarks: data.remarks,
    email: data.email,
    number: data.number,
    website: data.website,
    business_type: data.business_type?.value,
    // country: data.customer_country?.label,
    pocs: pocs,
    addresses: addresses,
    documents: [],
    bankdetails: [],
    is_tnc_not_needed: data.is_tnc_not_needed
  };
  return formData;
}

export function checkChipVariant(priority: string) {
  let chipVariant: chipVariantType = 'outlined';
  switch (priority) {
    case 'High':
      chipVariant = 'filled-red';
      break;
    case 'Low':
      chipVariant = 'filled-blue';
      break;
    case 'Medium':
      chipVariant = 'filled-blue';
      break;
    case 'Critical':
      chipVariant = 'filled';
      break;
    default:
      chipVariant = 'outlined';
  }
  return chipVariant;
}

export function isValidDate(dateString: any) {
  return !isNaN(Date.parse(dateString));
}

export function getDiffBetweenTwoDates(date1: Date, date2: Date, type: any = 'days') {
  const a = moment(date1);
  const b = moment(date2);
  return a.diff(b, type);
}

export function getFormattedDate(date: any) {
  if (date) {
    return format(endOfDay(new Date(date).getTime()), DATE_FORMAT);
  } else {
    return null;
  }
}

export function getCommaSepratedValue(value: string) {
  if (value) {
    value = Number(value).toFixed(2).toString();
    const parts = value.split('.');
    return Number(parts[0]).toLocaleString() + '.' + parts[1];
  } else {
    return null;
  }
}

export const TASK_STATUS_MAPPING = [
  {
    state: 0,
    isCompleted: null
  },
  {
    state: 1,
    isCompleted: 'False'
  },
  {
    state: 2,
    isCompleted: 'True'
  }
];

export function getFormattedTime(time: any) {
  if (time) {
    return moment(time).format('H:mm');
  } else {
    return null;
  }
}

export function getReadablePaymentTerm(data: any) {
  if (data?.length) {
    const readablePaymentTerm: any = [];
    data.forEach((item: any) => {
      readablePaymentTerm.push(
        `${item.percentage}% ${item.payment_term?.payment_term || item?.payment_term}  ${
          item.days ? `${item.days} days` : ''
        }`
      );
    });
    return readablePaymentTerm;
  } else {
    return [];
  }
}

export function generateReadablePackingDetails(data: any) {
  if (data?.length) {
    const readablePackingDetails: any = [];
    data.forEach((item: any) => {
      readablePackingDetails.push(
        `${item.no_of_package} X ${item.weight} ${item.unit?.label ?? item.unit_of_weight} ${
          item.type?.label ?? item.type_label
        }`
      );
    });
    return readablePackingDetails;
  } else {
    return [];
  }
}

export function generateQuantityFromPackingDetails(data: any, unitOfWeightToBeConverted?: any) {
  try {
    if (data?.length) {
      let quantity = 0;
      data.forEach((item: any) => {
        if (item.unit === 'MT' || item?.unit?.value === 'MT' || item?.unit_of_weight === 'MT')
          quantity += parseInt(item.no_of_package) * parseInt(item.weight) * MT_KG_FACTOR;
        else if (item.unit === 'LB' || item?.unit?.value === 'LB' || item?.unit_of_weight === 'LB')
          quantity += (parseInt(item.no_of_package) * parseInt(item.weight)) / LBS_KG_FACTOR;
        else if (item.unit === 'OZ' || item?.unit?.value === 'OZ' || item?.unit_of_weight === 'OZ')
          quantity += (parseInt(item.no_of_package) * parseInt(item.weight)) / OZ_KG_FACTOR;
        else quantity += parseInt(item.no_of_package) * parseInt(item.weight);
      });
      if (
        unitOfWeightToBeConverted &&
        (unitOfWeightToBeConverted.value == 'MT' || unitOfWeightToBeConverted == 'MT')
      ) {
        quantity = quantity / MT_KG_FACTOR;
      } else if (
        unitOfWeightToBeConverted &&
        (unitOfWeightToBeConverted.value == 'LB' || unitOfWeightToBeConverted == 'LB')
      ) {
        quantity = quantity * LBS_KG_FACTOR;
      } else if (
        unitOfWeightToBeConverted &&
        (unitOfWeightToBeConverted.value == 'OZ' || unitOfWeightToBeConverted == 'OZ')
      ) {
        quantity = quantity * OZ_KG_FACTOR;
      }
      return parseFloat(quantity.toFixed(4));
    } else {
      return 0;
    }
  } catch (err) {
    return 0;
  }
}

export function getCustomerCountry(customer: ICustomerInfo) {
  const customerAddress = customer?.addresses.find(
    (address) => address?.address_type === ADDRESS_TYPE_CONSTANTS.OFFICE
  );
  return customerAddress?.country ?? '-';
}

export const sanitizeValue = (
  value: string | undefined | null,
  defaultAlternateValue = '-'
): string => value || defaultAlternateValue;

export function cropValues(data: any, limit = DEFAULT_CROP_LIMIT) {
  if (data) {
    return data.length > limit ? data.substring(0, limit) + '...' : data;
  } else {
    return null;
  }
}

export const formatPortOptions = (data: string[]) =>
  data &&
  data.map((data: any) => ({
    label: `${data.name} - ${data.country_name}`,
    value: `${data.id}`
  }));

export const validateEmail = (email: string) => {
  if (!email) return false;
  const regex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(email);
};

export const getPermissionFromLocalStorage = () => {
  const token = getToken('permissions');
  //Logout user if permission token is not present
  if (!token) {
    SessionService.clearSession();
    return;
  }
  return JSON.parse(token as any);
};

export const openDocumentSignedLink = async (slug: string) => {
  if (slug?.length) {
    const response = await getDocumentLink(slug);
    if (response?.success) {
      window.open(response.data?.url, '_blank');
    } else {
      notify({
        message: response?.error ?? 'Unable to open document',
        dismissible: true,
        severity: 'error'
      });
    }
  }
  return null;
};

export const chunkArray = (arr: any[], size: number) => {
  const chunkedArr = [];
  for (let i = 0; i < arr.length; i += size) {
    chunkedArr.push(arr.slice(i, i + size));
  }
  return chunkedArr;
};

type CallbackFunctionVariadicAnyReturn = (...args: any[]) => any;

export const executeFunctionByCondition = (
  condition: boolean,
  func: CallbackFunctionVariadicAnyReturn
) => {
  if (condition) func();
  return;
};

export const integerToWords = (num: number) => {
  if (!num) return null;
  switch (num) {
    case 1:
      return 'one';
    case 2:
      return 'two';
    case 3:
      return 'three';
    case 4:
      return 'four';
    case 5:
      return 'five';
    case 6:
      return 'six';
    case 7:
      return 'seven';
    case 8:
      return 'eight';
    case 9:
      return 'nine';
    default:
      return null;
  }
};

export function getDomainWithoutWWW() {
  let hostname = window.location.hostname;
  if (hostname.startsWith('www.')) {
    hostname = hostname.substring(4);
  }

  return hostname;
}

export const pushEventTracking = (name: string) => {
  const accessToken = getToken('access');
  const jwtData = parseJWT(accessToken);
  if (window && (window as any).dataLayer) {
    (window as any).dataLayer.push({
      event: name ?? 'NO_NAMED_EVENT',
      email: jwtData?.email ?? 'NO_EMAIL'
    });
  }
};

export const SUPPORT_EMAIL = 'support@elchemy.com';
export const LEADERSHIP_EMAIL = 'leadership@elchemy.com';

export const filterNonEmptyValues = (obj: any) => {
  const nonEmptyValues = Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      } else if (typeof value === 'string') {
        return value.trim().length > 0;
      } else if (typeof value === 'number') {
        return true; // Keep all numbers, including 0
      } else {
        return value != null; //Keep everything except null and undefined
      }
    })
  );
  return nonEmptyValues;
};

export const getValidEntriesLength = (obj: any) => {
  if (obj == null) return 0;
  return Object.values(obj).filter(
    (value) => value !== null && (typeof value !== 'string' || value.length > 0)
  ).length;
};

export const checkScreenType = () => {
  if (typeof window != undefined) {
    if (window.innerWidth > SCREEN_TYPE.desktop.maxSize) return SCREEN_TYPE.desktop.label;
    else if (window.innerWidth > SCREEN_TYPE.tablet.maxSize) return SCREEN_TYPE.tablet.label;
    else return SCREEN_TYPE.mobile.label;
  }
  return null;
};

export const parseOrderEditFlag = (data: any) => {
  let orderEditable = false;
  _.forOwn(data, (value, key) => {
    if (value) {
      orderEditable = true;
    }
  });
  return orderEditable;
};

export const paymentTermsValidation = (data: any) => {
  let totalPercentage = 0,
    validationMessage: any = {
      error: false,
      message: null
    };
  data?.forEach((item: any) => {
    totalPercentage += parseInt(item.percentage);
    if (item?.payment_term?.days && (!item?.days || parseInt(item?.days) <= 0)) {
      validationMessage = {
        error: true,
        message: `Enter valid number of days for ${item?.payment_term?.payment_term}`
      };
    }
  });
  if (totalPercentage !== 100) {
    validationMessage = {
      error: true,
      message: 'Total percentage in payment terms should be 100'
    };
  }
  return validationMessage;
};

export const calculateNetAmount = (data: any) => {
  if (!data) return 0;
  return (
    parseFloat(data.gross_invoice_amount) -
    parseFloat(data.tds_amount) +
    parseFloat(data.tcs_amount) +
    parseFloat(data.additional_charges_or_discounts)
  );
};

export const sortByUploadedOnNewToOld = (a: any, b: any) => {
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
};

export const sortByUploadedOnOldToNew = (a: any, b: any) => {
  return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
};

export const sortByDocumentTypeAZ = (a: any, b: any) => {
  return a.document_type_display_value.localeCompare(b.document_type_display_value);
};

export const sortByDocumentTypeZA = (a: any, b: any) => {
  return b.document_type_display_value.localeCompare(a.document_type_display_value);
};

export const getCommaSeperatedCurrencyAmount = (currency: string, amount: string | number) => {
  const Currency = CURRENCY.find((item: any) => item.label == currency);
  const formattedAmount = new Intl.NumberFormat(Currency?.code ?? 'en-US', {
    maximumFractionDigits: 2
  }).format(Number(amount));

  return `${currency} ${formattedAmount}`;
};

export function getConversionKey(targetUnit: string) {
  switch (targetUnit) {
    case 'KG':
      return 'convertToKg';
    case 'MT':
      return 'convertToMT';
    case 'LBS':
      return 'convertToLbs';
    case 'OZ':
      return 'convertToOz';
    case 'GALLON':
      return 'convertToGallon';
    default:
      return '';
  }
}

export function findWeightChildOption(unitValue: string) {
  for (const group of WEIGHT_SYSTEM) {
    const found = group?.childOptions?.find((child) => child.value === unitValue);
    if (found) return found;
  }
  return null;
}

export function convertWeight(weight: number, fromUnitValue: string, toUnitValue: string) {
  if (fromUnitValue === toUnitValue) return weight;

  const fromChild = findWeightChildOption(fromUnitValue);
  const toChild = findWeightChildOption(toUnitValue);

  if (!fromChild || !toChild) {
    return weight;
  }

  const conversionKey = getConversionKey(toChild.value);

  if (!conversionKey) {
    return weight;
  }

  const factor = fromChild[conversionKey];

  if (typeof factor !== 'number') {
    return weight;
  }
  return weight * factor;
}

export function convertPricePerUnit(
  pricePerUnit: number,
  fromUnitValue: string,
  toUnitValue: string
) {
  if (fromUnitValue === toUnitValue) return pricePerUnit;

  const fromChild = findWeightChildOption(fromUnitValue);
  const toChild = findWeightChildOption(toUnitValue);

  if (!fromChild || !toChild) {
    return pricePerUnit;
  }

  const fromGroup = WEIGHT_SYSTEM.find((group) =>
    group.childOptions?.some((child) => child.value === fromUnitValue)
  );
  const toGroup = WEIGHT_SYSTEM.find((group) =>
    group.childOptions?.some((child) => child.value === toUnitValue)
  );

  if (fromGroup !== toGroup) {
    return pricePerUnit;
  }

  const conversionKey = getConversionKey(toChild.value);

  if (!conversionKey) {
    return pricePerUnit;
  }

  const factor = fromChild[conversionKey];

  if (typeof factor !== 'number') {
    return pricePerUnit;
  }
  return pricePerUnit / factor;
}

export const calculateExWorksPrice = (
  basePriceCurrency: ICurrency,
  currency1: ICurrency,
  basePrice: number,
  fxRate: number,
  exWorksPriceCurrency: ICurrency,
  basePriceWeightUnit: IWeightChildOption,
  finalExWorksPriceWeightUnit: IWeightChildOption,
  primaryUnit: IWeightChildOption
): number => {
  const basePriceInCurrency1 =
    basePriceCurrency?.value === currency1?.value
      ? Number(basePrice || 0)
      : Number(basePrice || 0) / fxRate;

  const basePriceInPrimaryWeightUnit = convertPricePerUnit(
    basePriceInCurrency1,
    basePriceWeightUnit?.value,
    primaryUnit?.value
  );

  const finalExWorksPrice =
    exWorksPriceCurrency?.value === currency1?.value
      ? Number(basePriceInPrimaryWeightUnit || 0) * BASE_PRICE_COEFFICIENT
      : Number(basePriceInPrimaryWeightUnit || 0) * BASE_PRICE_COEFFICIENT * (fxRate || 1);
  return convertPricePerUnit(
    finalExWorksPrice,
    primaryUnit?.value,
    finalExWorksPriceWeightUnit?.value
  );
};

export const calculateFobPrice = (
  localFreight: number,
  localFreightCurrency: ICurrency,
  portAndClearanceExpenses: number,
  portAndClearanceExpensesCurrency: ICurrency,
  weightInContainerInPrimaryUnit: number,
  fxRate: number,
  currency1: ICurrency,
  fobPriceCurrency: ICurrency,
  finalExWorksPrice: number,
  finalExWorksPriceCurrency: ICurrency,
  finalExWorksPriceWeightUnit: IWeightChildOption,
  fobPriceWeightUnit: IWeightChildOption,
  primaryUnit: IWeightChildOption
): number => {
  const finalLocalFreight =
    localFreightCurrency?.value === currency1.value
      ? Number(localFreight || 0)
      : Number(localFreight || 0) / fxRate;
  const finalPortAndClearanceExpenses =
    portAndClearanceExpensesCurrency?.value === currency1.value
      ? Number(portAndClearanceExpenses || 0)
      : Number(portAndClearanceExpenses || 0) / fxRate;

  const portAndLocalFreightExpenses =
    weightInContainerInPrimaryUnit > 0
      ? (
          (finalLocalFreight + finalPortAndClearanceExpenses) /
          weightInContainerInPrimaryUnit
        ).toFixed(2)
      : '0';

  const finalExWorksPriceInCurrency1 =
    finalExWorksPriceCurrency?.value === currency1?.value
      ? Number(finalExWorksPrice || 0)
      : Number(finalExWorksPrice || 0) / fxRate;

  const finalExWorksPriceInPrimaryUnit = convertPricePerUnit(
    finalExWorksPriceInCurrency1,
    finalExWorksPriceWeightUnit?.value,
    primaryUnit?.value
  );
  const finalFobPriceCurrency =
    fobPriceCurrency?.value === currency1?.value
      ? Number(portAndLocalFreightExpenses || 0) + Number(finalExWorksPriceInPrimaryUnit || 0)
      : (Number(portAndLocalFreightExpenses || 0) + Number(finalExWorksPriceInPrimaryUnit || 0)) *
        fxRate;
  return convertPricePerUnit(finalFobPriceCurrency, primaryUnit?.value, fobPriceWeightUnit?.value);
};

export const calculateCFRPrice = (
  totalFreight: number,
  totalFreightCurrency: ICurrency,
  weightInContainerInPrimaryUnit: number,
  fxRate: number,
  cfrPriceCurrency: ICurrency,
  currency1: ICurrency,
  fobPrice: number,
  fobPriceCurrency: ICurrency,
  fobPriceWeightUnit: IWeightChildOption,
  cfrPriceWeightUnit: IWeightChildOption,
  primaryUnit: IWeightChildOption
): number => {
  const totalFreightInCurrency1 =
    totalFreightCurrency?.value === currency1?.value
      ? Number(totalFreight ?? 0)
      : Number(totalFreight || 0) / fxRate;

  const fobPriceInCurrency1 =
    fobPriceCurrency?.value === currency1?.value
      ? Number(fobPrice || 0)
      : Number(fobPrice || 0) / fxRate;

  const fobPriceInPrimaryUnit = convertPricePerUnit(
    fobPriceInCurrency1,
    fobPriceWeightUnit?.value,
    primaryUnit?.value
  );

  const freightPerDealUnit =
    Number(totalFreightInCurrency1 || 0) / (weightInContainerInPrimaryUnit || 1);

  const finalCfrPrice =
    cfrPriceCurrency?.value === currency1?.value
      ? Number(freightPerDealUnit || 0) + Number(fobPriceInPrimaryUnit || 0)
      : (Number(freightPerDealUnit || 0) + Number(fobPriceInPrimaryUnit || 0)) * fxRate;

  return convertPricePerUnit(finalCfrPrice, primaryUnit?.value, cfrPriceWeightUnit?.value);
};

export const calculateCIFPrice = (
  fxRate: number,
  currency1: ICurrency,
  cfrPrice: number,
  cfrPriceCurrency: ICurrency,
  insuranceCost: number,
  cifPriceCurrency: ICurrency,
  cfrPriceWeightUnit: IWeightChildOption,
  cifPriceWeightUnit: IWeightChildOption,
  primaryUnit: IWeightChildOption
): number => {
  const cfrPriceInCurrency1 =
    cfrPriceCurrency?.value === currency1?.value ? cfrPrice : cfrPrice / fxRate;

  const cfrPriceInPrimaryUnit = convertPricePerUnit(
    cfrPriceInCurrency1,
    cfrPriceWeightUnit?.value,
    primaryUnit?.value
  );

  const totalInsuranceCost = ((insuranceCost ?? 0) * Number(cfrPriceInPrimaryUnit)) / 100;

  const finalCifPrice =
    cifPriceCurrency?.value === currency1?.value
      ? Number(cfrPriceInPrimaryUnit || 0) + Number(totalInsuranceCost || 0)
      : (Number(cfrPriceInPrimaryUnit || 0) + Number(totalInsuranceCost || 0)) * fxRate;

  return convertPricePerUnit(finalCifPrice, primaryUnit?.value, cifPriceWeightUnit?.value);
};

// Cost per primary unit in currency1 (CIF + additional + switch BL + tariff), before finance/profit/discount
export const calculateQuoteCost = (
  cifPrice: number,
  cifPriceCurrency: ICurrency,
  cifPriceWeightUnit: IWeightChildOption,
  additionalCost: number,
  additionalCostCurrency: ICurrency,
  switchBLCostWatch: number,
  switchBLCostCurrencyWatch: ICurrency,
  tariff: number,
  currency1: ICurrency,
  fxRate: number,
  primaryUnit: IWeightChildOption
): number => {
  const cifPriceInCurrency1 =
    cifPriceCurrency?.value === currency1?.value
      ? Number(cifPrice || 0)
      : Number(cifPrice || 0) / fxRate;

  // Step 2: Convert CIF price to primary weight unit
  const cifPriceInPrimaryUnit = convertPricePerUnit(
    cifPriceInCurrency1,
    cifPriceWeightUnit?.value,
    primaryUnit?.value
  );

  // Step 3: Convert additional costs to currency1
  const additionalCostInCurrency1 =
    additionalCostCurrency?.value === currency1?.value
      ? Number(additionalCost || 0)
      : Number(additionalCost || 0) / fxRate;

  const switchBLCostInCurrency1 =
    switchBLCostCurrencyWatch?.value === currency1?.value
      ? Number(switchBLCostWatch || 0)
      : Number(switchBLCostWatch || 0) / fxRate;

  // Step 4: Sum all costs in primary unit
  const sumCIFandAdditionalCost =
    Number(cifPriceInPrimaryUnit || 0) +
    Number(additionalCostInCurrency1 || 0) +
    Number(switchBLCostInCurrency1 || 0);

  // Step 4.5: Apply tariff (after additional costs, before finance/profit/discount)
  const tariffAmount = (Number(sumCIFandAdditionalCost || 0) * Number(tariff || 0)) / 100;
  return Number(sumCIFandAdditionalCost) + Number(tariffAmount);
};

export const calculateQuotedPrice = (
  cifPrice: number,
  cifPriceCurrency: ICurrency,
  cifPriceWeightUnit: IWeightChildOption,
  additionalCost: number,
  additionalCostCurrency: ICurrency,
  switchBLCostWatch: number,
  switchBLCostCurrencyWatch: ICurrency,
  financeCost: number,
  profitMarginExpected: number,
  discount: number,
  tariff: number,
  currency1: ICurrency,
  fxRate: number,
  primaryUnit: IWeightChildOption,
  finalQuoteSellingPriceWeightUnit: IWeightChildOption
): number => {
  const sumWithTariff = calculateQuoteCost(
    cifPrice,
    cifPriceCurrency,
    cifPriceWeightUnit,
    additionalCost,
    additionalCostCurrency,
    switchBLCostWatch,
    switchBLCostCurrencyWatch,
    tariff,
    currency1,
    fxRate,
    primaryUnit
  );

  // Step 5: Calculate multipliers and discount
  const financeMultiplier = 1 + Number(financeCost || 0) / 100;
  const profitMarginMultiplier = 1 + Number(profitMarginExpected || 0) / 100;
  const discountAmount = Number(sumWithTariff || 0) * (Number(discount || 0) / 100);

  // Step 6: Calculate final quote selling price in primary unit
  const finalQuoteSellingPriceInPrimaryUnit =
    sumWithTariff * financeMultiplier * profitMarginMultiplier - discountAmount;

  // Step 7: Convert to target weight unit
  const finalQuoteSellingPrice = convertPricePerUnit(
    finalQuoteSellingPriceInPrimaryUnit,
    primaryUnit?.value,
    finalQuoteSellingPriceWeightUnit?.value
  );

  return finalQuoteSellingPrice;
};

export const calculateDdpPrice = (
  cifPriceWatch: number,
  cifPriceCurrencyWatch: ICurrency,
  cifPriceWeightUnitWatch: IWeightChildOption,
  isfFilingWatch: number,
  isfFilingCurrencyWatch: ICurrency,
  customClearanceWatch: number,
  drayageChargeWatch: number,
  drayageChargeCurrencyWatch: ICurrency,
  fxRateWatch: number,
  primaryUnitWatch: IWeightChildOption,
  warehousingCostPrice: number,
  warehousingCostCurrency: ICurrency,
  ddpPriceWeightUnitWatch: IWeightChildOption,
  ddpPriceCurrencyWatch: ICurrency
): number => {
  const cifPriceInCurrency1 =
    cifPriceCurrencyWatch?.value === ddpPriceCurrencyWatch?.value
      ? Number(cifPriceWatch || 0)
      : Number(cifPriceWatch || 0) / fxRateWatch;

  // Step 2: Convert CIF price to primary weight unit
  const cifPriceInPrimaryUnit = convertPricePerUnit(
    cifPriceInCurrency1,
    cifPriceWeightUnitWatch?.value,
    primaryUnitWatch?.value
  );

  // Step 3: Convert ISF Filing cost to currency1
  const isfFilingCostInCurrency1 =
    isfFilingCurrencyWatch?.value === ddpPriceCurrencyWatch?.value
      ? Number(isfFilingWatch || 0)
      : Number(isfFilingWatch || 0) / fxRateWatch;

  // Step 4: Convert Custom Clearance cost is a percentage value, which if x% of cif price
  const customClearanceCostInCurrency1 =
    (Number(cifPriceInPrimaryUnit || 0) * customClearanceWatch) / 100;

  // Step 5: Convert Drayage Charge cost to currency1
  const drayageChargeInCurrency1 =
    drayageChargeCurrencyWatch?.value === ddpPriceCurrencyWatch?.value
      ? Number(drayageChargeWatch || 0)
      : Number(drayageChargeWatch || 0) / fxRateWatch;

  const warehousingChargesInCurrency1 =
    warehousingCostCurrency?.value === ddpPriceCurrencyWatch?.value
      ? Number(warehousingCostPrice || 0)
      : Number(warehousingCostPrice || 0) / fxRateWatch;

  // Step 6: Sum all costs in primary unit
  const sumCIFandAdditionalCost =
    Number(cifPriceInPrimaryUnit || 0) +
    Number(isfFilingCostInCurrency1 || 0) +
    Number(customClearanceCostInCurrency1 || 0) +
    Number(drayageChargeInCurrency1 || 0) +
    Number(warehousingChargesInCurrency1 || 0);

  // Step 7: Convert to target weight unit
  const finalDDPPrice = convertPricePerUnit(
    sumCIFandAdditionalCost,
    primaryUnitWatch?.value,
    ddpPriceWeightUnitWatch?.value
  );

  return finalDDPPrice;
};

// Cost per primary unit (base + freight + warehousing + insurance + additional), before finance/profit/discount
export const calculateDomesticQuoteCost = (
  basePrice: number,
  basePriceWeightUnit: IWeightChildOption,
  weight: number,
  insuranceCost: number,
  additionalCost: number,
  localFreight: number,
  warehouseCharges: number,
  primaryUnit: IWeightChildOption
): number => {
  const basePriceInPrimaryUnit = convertPricePerUnit(
    Number(basePrice),
    basePriceWeightUnit?.value,
    primaryUnit?.value
  );

  const BaseFreightAndWarehouse =
    basePriceInPrimaryUnit +
    ((Number(localFreight) || 0) + (Number(warehouseCharges) || 0)) / (Number(weight) || 1);

  const totalInsuranceCost = (Number(insuranceCost || 0) * Number(BaseFreightAndWarehouse)) / 100;

  return Number(totalInsuranceCost) + Number(BaseFreightAndWarehouse) + Number(additionalCost || 0);
};

export interface ICMValues {
  cm1: number;
  cm1Percent: number;
  cm2: number;
  cm2Percent: number;
  financeCost: number;
}

export const EMPTY_CM_VALUES: ICMValues = {
  cm1: 0,
  cm1Percent: 0,
  cm2: 0,
  cm2Percent: 0,
  financeCost: 0
};

export const roundTo2 = (value: number) => (Number.isFinite(value) ? Number(value.toFixed(2)) : 0);

// CM1 / CM2 as absolute amounts for the whole deal quantity. Display only, never sent in payload.
// TSP = SP * Q, CM1 = TSP - CP * Q, FC = CP * FC% * Q, CM2 = CM1 - FC; CM % is of the total selling price
// e.g. CP 1000/MT, SP 1155/MT (FC 5%, profit 10%), 100 MT -> CM1 15,500 - FC 5,000 = CM2 10,500
export const calculateCMValues = (
  costPerPrimaryUnit: number,
  sellingPricePerPrimaryUnit: number,
  financeCostPercent: number,
  dealQuantityInPrimaryUnit: number
): ICMValues => {
  const quantity = Number(dealQuantityInPrimaryUnit || 0);
  // Cost is rounded per unit like saved prices. Selling price comes in already rounded to the
  // quoted price (2 decimals in its own unit, e.g. 0.79 per LBS), so it is not rounded again here.
  const totalCost = roundTo2(Number(costPerPrimaryUnit || 0)) * quantity;
  const totalSellingPrice = Number(sellingPricePerPrimaryUnit || 0) * quantity;
  const cm1 = totalSellingPrice - totalCost;
  const financeCost = (totalCost * Number(financeCostPercent || 0)) / 100;
  const cm2 = cm1 - financeCost;
  const percentOfSellingPrice = (value: number) =>
    totalSellingPrice ? roundTo2((value / totalSellingPrice) * 100) : 0;

  return {
    cm1: roundTo2(cm1),
    cm1Percent: percentOfSellingPrice(cm1),
    cm2: roundTo2(cm2),
    cm2Percent: percentOfSellingPrice(cm2),
    financeCost: roundTo2(financeCost)
  };
};

// e.g. "USD 1,200 (3%)"
export const formatCMValue = (amount: number, percent: number, currency?: string): string =>
  currency ? `${getCommaSeperatedCurrencyAmount(currency, amount ?? 0)} (${percent ?? 0}%)` : '-';

// Green for positive, red for negative, blue for zero
export const getCMTone = (amount: number): 'positive' | 'negative' | 'zero' =>
  amount > 0 ? 'positive' : amount < 0 ? 'negative' : 'zero';

// Toast the first react-hook-form validation error, so a failing field that is scrolled out of view isn't silent
export const notifyFirstFormError = (errors: any) => {
  const findMessage = (node: any): string | undefined => {
    if (!node || typeof node !== 'object') return undefined;
    if (typeof node.message === 'string' && node.message) return node.message;
    for (const [key, value] of Object.entries(node)) {
      if (key === 'ref') continue;
      const message = findMessage(value);
      if (message) return message;
    }
    return undefined;
  };
  notify({ severity: 'error', message: findMessage(errors) ?? 'Please fill all required fields' });
};

const getIncotermPriceForQuote = (quote: any) => {
  switch (quote?.deal_incoterm) {
    case 'EXW':
      return quote?.ex_works_price;
    case 'FOB':
      return quote?.fob_price;
    case 'CFR':
      return quote?.cfr_price;
    case 'DDP':
      return quote?.ddp_price;
    default:
      return quote?.cif_price;
  }
};

// CM1 / CM2 for a saved quote (listing / detail pages), using the same calculation as the quote forms.
// For consolidation pass the product as `quote` and the consolidation quote as `consolidationQuote`.
export const calculateSavedQuoteCMValues = (
  quote: any,
  enquiry: {
    currency1: ICurrency | null;
    fxRate: number;
    primaryUnit: IWeightChildOption | null;
    isDomestic?: boolean;
  },
  consolidationQuote?: any
): ICMValues => {
  const { currency1, fxRate, primaryUnit, isDomestic } = enquiry;
  const findCurrency = (value: string) =>
    CURRENCY.find((item) => item.value === value) as ICurrency;
  const dealQuantityInPrimaryUnit = convertWeight(
    Number(quote?.quantity || 0),
    quote?.quantity_unit,
    primaryUnit?.value ?? ''
  );

  let quoteCost = 0;
  if (isDomestic) {
    const totalWeightInPrimaryUnit = convertWeight(
      Number(quote?.weight_one_container || 0),
      quote?.weight_one_container_unit,
      primaryUnit?.value ?? ''
    );
    const weightToUse =
      dealQuantityInPrimaryUnit == 0
        ? totalWeightInPrimaryUnit
        : totalWeightInPrimaryUnit == 0
        ? dealQuantityInPrimaryUnit
        : Math.min(dealQuantityInPrimaryUnit, totalWeightInPrimaryUnit);
    quoteCost = calculateDomesticQuoteCost(
      Number(quote?.base_price?.value || 0),
      findWeightChildOption(quote?.base_price?.unit_of_weight) as IWeightChildOption,
      weightToUse,
      Number(quote?.insurance_cost || 0),
      Number(quote?.additional_cost?.value || 0),
      Number(quote?.total_local_freight?.value || 0),
      Number(quote?.warehousing_cost?.value || 0),
      primaryUnit as IWeightChildOption
    );
  } else {
    // Prices are on the product; for consolidation the incoterm is on the consolidation quote
    const priceSource =
      getIncotermPriceForQuote({
        ...quote,
        deal_incoterm: (consolidationQuote ?? quote)?.deal_incoterm
      }) ?? {};
    const switchBlCost = (consolidationQuote ?? quote)?.switch_bl_cost;
    quoteCost = calculateQuoteCost(
      Number(priceSource?.value || 0),
      findCurrency(priceSource?.currency),
      findWeightChildOption(priceSource?.unit_of_weight) as IWeightChildOption,
      Number(quote?.additional_cost?.value || 0),
      findCurrency(quote?.additional_cost?.currency),
      Number(switchBlCost?.value || 0),
      findCurrency(switchBlCost?.currency),
      consolidationQuote ? 0 : Number(quote?.tariff || 0),
      currency1 as ICurrency,
      Number(fxRate) || 1,
      primaryUnit as IWeightChildOption
    );
  }

  // SP is the saved final price as quoted (2 decimals in its own currency and unit), in Currency 1 per primary unit.
  // Domestic prices are not currency-converted, matching the domestic price calculation.
  const finalPrice = quote?.final_price;
  const quotedFinalPrice = roundTo2(Number(finalPrice?.value || 0));
  const finalPriceInCurrency1 =
    isDomestic || !finalPrice?.currency || finalPrice.currency === currency1?.value
      ? quotedFinalPrice
      : quotedFinalPrice / (Number(fxRate) || 1);
  const sellingPricePerPrimaryUnit = convertPricePerUnit(
    finalPriceInCurrency1,
    finalPrice?.unit_of_weight ?? primaryUnit?.value ?? '',
    primaryUnit?.value ?? ''
  );

  return calculateCMValues(
    quoteCost,
    sellingPricePerPrimaryUnit,
    (consolidationQuote ?? quote)?.finance_cost,
    dealQuantityInPrimaryUnit
  );
};

export const calculateDomesticFinalQuoteSellingPrice = (
  basePrice: number,
  basePriceWeightUnit: IWeightChildOption,
  weight: number,
  insuranceCost: number,
  discount: number,
  profitMargin: number,
  financeCost: number,
  additionalCost: number,
  localFreight: number,
  warehouseCharges: number,
  domesticFinalQuoteSellingPriceWeightUnit: IWeightChildOption,
  domesticFinalQuoteSellingPriceCurrency: ICurrency,
  primaryUnit: IWeightChildOption
): number => {
  const BaseFreightWarehouseAdditional = calculateDomesticQuoteCost(
    basePrice,
    basePriceWeightUnit,
    weight,
    insuranceCost,
    additionalCost,
    localFreight,
    warehouseCharges,
    primaryUnit
  );

  const financeMultiplier = 1 + Number(financeCost || 0) / 100;
  const profitMarginMultiplier = 1 + Number(profitMargin || 0) / 100;
  const discountAmount = Number(BaseFreightWarehouseAdditional) * (Number(discount || 0) / 100);

  const finalDomesticQuoteSellingPriceInPrimaryUnit =
    Number(BaseFreightWarehouseAdditional) *
      Number(financeMultiplier) *
      Number(profitMarginMultiplier) -
    Number(discountAmount);

  const finalDomesticQuoteSellingPrice = convertPricePerUnit(
    Number(finalDomesticQuoteSellingPriceInPrimaryUnit),
    primaryUnit?.value,
    domesticFinalQuoteSellingPriceWeightUnit?.value
  );

  return Number(finalDomesticQuoteSellingPrice);
};

export function hasNonZeroPriceComponent(reqBody: any) {
  if (!reqBody.quotes || !Array.isArray(reqBody.quotes) || reqBody.quotes.length === 0) {
    return false;
  }

  for (const quote of reqBody.quotes) {
    if (!quote.costs || typeof quote.costs !== 'object') {
      continue;
    }

    const priceComponentKeys = [
      'base_price',
      'fob_price',
      'cfr_price',
      'cif_price',
      'ddp_price',
      'ex_works_price'
    ];

    for (const key of priceComponentKeys) {
      const priceComponent = quote.costs[key];

      if (!priceComponent || typeof priceComponent !== 'object') {
        continue;
      }

      if (priceComponent.value !== undefined && priceComponent.value !== null) {
        const numericValue = Number(priceComponent.value);
        if (!isNaN(numericValue) && numericValue > 0) {
          return true;
        }
      }
    }
  }

  return false;
}

export const shouldShowContainer = (mode?: string): boolean =>
  mode === TRANSPORTATION_MODE_ENUM.SEA;

export const shouldShowPorts = (supplyType?: string): boolean =>
  supplyType !== SUPPLY_TYPE_ENUM.DOMESTIC;

export const showTrademarkOptions = (featureList: string[]): boolean => {
  if (featureList && featureList.length > 0) {
    return featureList.includes('show_product_trademark');
  }
  return false;
};

export const displayProductName = (
  showTrademark = true,
  showProductName = false,
  trademark: string,
  productName: string
) => {
  if (showProductName) {
    if (showTrademark) {
      if (trademark && trademark.length > 0) {
        return `${trademark} ${productName ? `(${productName})` : ``}`;
      } else {
        return productName;
      }
    } else {
      return productName;
    }
  } else if (trademark && trademark.length > 0 && showTrademark) {
    return trademark;
  } else {
    return productName;
  }
};
export const calculateAmount = (
  quantity: string | number,
  rate: string | number,
  discount: string | number,
  discount_type: any
) => {
  const quantityNum = Number(quantity ?? '0');
  const rateNum = Number(rate ?? '0');
  const discountNum = Number(discount ?? '0');

  if (
    discount_type?.value === DISCOUNT_ENUM.PERCENTAGE ||
    discount_type === DISCOUNT_ENUM.PERCENTAGE
  ) {
    return quantityNum * rateNum * (1 - discountNum / 100);
  }

  if (discount_type?.value === DISCOUNT_ENUM.FLAT || discount_type === DISCOUNT_ENUM.FLAT) {
    return quantityNum * (rateNum - discountNum);
  }

  return quantityNum * rateNum;
};

/**
 * Generates a secure random password string.
 * Ensures at least one character from each selected character set is included.
 * Defaults: 10 chars, includes upper, lower, digits, and special characters.
 */
export function generateSecurePassword(
  length = 10,
  options?: {
    includeUppercase?: boolean;
    includeLowercase?: boolean;
    includeDigits?: boolean;
    includeSpecial?: boolean;
  }
): string {
  const includeUppercase = options?.includeUppercase ?? true;
  const includeLowercase = options?.includeLowercase ?? true;
  const includeDigits = options?.includeDigits ?? true;
  const includeSpecial = options?.includeSpecial ?? true;

  const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const LOWER = 'abcdefghijklmnopqrstuvwxyz';
  const DIGITS = '0123456789';
  const SPECIAL = '!@#$%^&*()-_=+[]{};:,<.>/?';

  const selectedSets: string[] = [];
  if (includeUppercase) selectedSets.push(UPPER);
  if (includeLowercase) selectedSets.push(LOWER);
  if (includeDigits) selectedSets.push(DIGITS);
  if (includeSpecial) selectedSets.push(SPECIAL);

  const allChars = selectedSets.join('');
  if (!allChars) return '';

  const getRandomInt = (max: number) => {
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
      const array = new Uint32Array(1);
      window.crypto.getRandomValues(array);
      return array[0] % max;
    }
    return Math.floor(Math.random() * max);
  };

  const resultChars: string[] = [];
  // Ensure at least one char from each set
  selectedSets.forEach((set) => {
    resultChars.push(set[getRandomInt(set.length)]);
  });

  // Fill the remaining length
  for (let i = resultChars.length; i < Math.max(length, selectedSets.length); i += 1) {
    resultChars.push(allChars[getRandomInt(allChars.length)]);
  }

  // Fisher–Yates shuffle
  for (let i = resultChars.length - 1; i > 0; i -= 1) {
    const j = getRandomInt(i + 1);
    const tmp = resultChars[i];
    resultChars[i] = resultChars[j];
    resultChars[j] = tmp;
  }

  return resultChars.join('');
}

export const isNotFutureDateValidator = (message = 'Date cannot be in the future') => ({
  name: 'not-future-date',
  message,
  test(value: any) {
    if (!value) return true;
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return value <= today;
  }
});
/**
 * Replace template variables {{variable}} with their actual values
 * Used for displaying remarks with filled-in variable values
 * @param text - Template text with {{variable}} placeholders (e.g., "{{no_of_days}} days free days at port")
 * @param variables - Record of variables with their values (e.g., { no_of_days: { type: "number", value: "12" } })
 * @returns Text with variables replaced by their values (e.g., "12 days free days at port")
 */
export const replaceTemplateVariables = (
  text: string | null | undefined,
  variables: Record<string, { type: string | null; value: string | null }>
): string => {
  // Handle null/undefined text
  if (!text) {
    return '';
  }

  if (!variables || Object.keys(variables).length === 0) {
    return text;
  }

  let result = text;
  const variablePattern = /\{\{(\w+)\}\}/g;

  result = result.replace(variablePattern, (match, variableName) => {
    const variable = variables[variableName];
    if (variable && variable.value) {
      return variable.value;
    }
    return match; // Keep placeholder if no value
  });

  return result;
};

/**
 * Format remarks for display by processing default remarks with variable replacement
 * and combining with custom remarks
 * @param remarksData - Remarks object containing custom_remarks and default_remarks
 * @returns Array of formatted remark strings
 */
export const formatRemarksForDisplay = (remarksData: {
  custom_remarks?: string[];
  default_remarks?: Array<{
    is_checked: boolean;
    full_text: string;
    variables?: Record<string, { type: string | null; value: string | null }>;
  }>;
}): string | null => {
  if (!remarksData) {
    return null;
  }
  // Process default remarks with variable replacement
  const defaultRemarksArray =
    remarksData.default_remarks
      ?.filter((remark) => remark.is_checked && remark.full_text)
      .map((remark) => replaceTemplateVariables(remark.full_text, remark.variables || {})) || [];

  const customRemarksArray = remarksData.custom_remarks || [];

  // Combine default remarks first, then custom remarks
  const allRemarks = [...defaultRemarksArray, ...customRemarksArray].filter(Boolean);
  if (allRemarks.length === 0) {
    return null;
  }
  return allRemarks.join('. ');
};
